
import { Injectable, inject } from '@angular/core';
import { isSupabaseConfigured, getSupabase } from '../firebase';
import { ProductService } from './product.service';
import { CustomerService } from './customer.service';
import { UserService } from './user.service';
import { OrderService } from './order.service';
import { InventoryService } from './inventory.service';
import { PrescriptionService } from './prescription.service';
import { LoyaltyService } from './loyalty.service';
import { ReportingService } from './reporting.service';
import { ShiftService } from './shift.service';
import { BillingService } from './billing.service';
import { CategoryService } from './category.service';
import { AccountingService } from './accounting.service'; 
import { Product, Customer, Ticket, SalesSummary, Order, AppUser, LoyaltyReward, LoyaltyTier, InventoryLog, AccountingSummary, BestsellerItem, Prescription, PrescriptionDetail, Plan, Subscription, Permissions, Shift, SalesSummaryReport, CategorySalesReport, EmployeeSalesReport, PaymentMethodSalesReport, DiscountReport, TaxReport, ShiftReport, TimeClockEntry, HoursWorkedSummary, PortalSettings, FeatureSettings, ReceiptSettings, Category, Store, PosDevice, CategoryQuota, TaxSettings, Account, LoyaltySettings, ClinicalSymptomStats, DoctorPerformanceStats, ClinicSettings, StorePartner, StoreWallet, WalletTransaction } from '../models/index';
import { SupabaseClient } from '@supabase/supabase-js';

// Define VitalsData type here for use in createPrescription
type VitalsData = Partial<Pick<Prescription, 'temperature_c' | 'pulse_rate' | 'respiration_rate' | 'blood_pressure' | 'weight_kg' | 'height_cm'>>;

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private products = inject(ProductService);
  private customers = inject(CustomerService);
  private users = inject(UserService);
  private orders = inject(OrderService);
  private inventory = inject(InventoryService);
  private prescriptions = inject(PrescriptionService);
  private loyalty = inject(LoyaltyService);
  private reporting = inject(ReportingService);
  private shifts = inject(ShiftService);
  private billing = inject(BillingService);
  private categories = inject(CategoryService);
  private accounting = inject(AccountingService); 
  private supabase: SupabaseClient | null = getSupabase();

  isSupabaseEnabled(): boolean {
    return isSupabaseConfigured();
  }

  // --- Portal & Feature Settings ---
  getPortalSettings(): Promise<PortalSettings | null> { return this.users.getPortalSettings(); }
  updatePortalSettings(settings: PortalSettings): Promise<void> { return this.users.updatePortalSettings(settings); }
  getFeatureSettings(): Promise<FeatureSettings | null> { return this.users.getFeatureSettings(); }
  updateFeatureSettings(settings: FeatureSettings): Promise<void> { return this.users.updateFeatureSettings(settings); }
  getReceiptSettings(): Promise<ReceiptSettings | null> { return this.users.getReceiptSettings(); }
  updateReceiptSettings(settings: ReceiptSettings): Promise<void> { return this.users.updateReceiptSettings(settings); }
  getTaxSettings(): Promise<TaxSettings | null> { return this.users.getTaxSettings(); }
  updateTaxSettings(settings: TaxSettings): Promise<void> { return this.users.updateTaxSettings(settings); }
  getLoyaltySettings(): Promise<LoyaltySettings | null> { return this.users.getLoyaltySettings(); }
  updateLoyaltySettings(settings: LoyaltySettings): Promise<void> { return this.users.updateLoyaltySettings(settings); }
  
  getClinicSettings(): Promise<ClinicSettings | null> { return this.users.getClinicSettings(); }
  updateClinicSettings(settings: ClinicSettings): Promise<void> { return this.users.updateClinicSettings(settings); }

  // --- Category Methods ---
  getCategories(): Promise<Category[]> { return this.categories.getCategories(); }
  addCategory(name: string): Promise<Category> { return this.categories.addCategory(name); }
  updateCategory(id: string, name: string): Promise<Category> { return this.categories.updateCategory(id, name); }
  deleteCategory(id: string): Promise<void> { return this.categories.deleteCategory(id); }

  // --- Product Methods ---
  getProducts(): Promise<Product[]> { return this.products.getProducts(); }
  addProduct(productData: Omit<Product, 'id' | 'createdAt'>): Promise<Product> { return this.products.addProduct(productData); }
  updateProduct(updatedProduct: Product): Promise<Product> { return this.products.updateProduct(updatedProduct); }
  deleteProduct(productId: string): Promise<void> { return this.products.deleteProduct(productId); }
  batchAddProducts(products: Omit<Product, 'id' | 'createdAt'>[]): Promise<{ success: boolean; message: string }> { return this.products.batchAddProducts(products); }

  // --- Customer Methods ---
  getCustomers(): Promise<Customer[]> { return this.customers.getCustomers(); }
  addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'hn'>): Promise<Customer> { return this.customers.addCustomer(customerData); }
  updateCustomer(updatedCustomer: Customer): Promise<Customer> { return this.customers.updateCustomer(updatedCustomer); }
  deleteCustomer(customerId: string): Promise<void> { return this.customers.deleteCustomer(customerId); }
  getCustomerByCardUid(cardUid: string): Promise<Customer | null> { return this.customers.getCustomerByCardUid(cardUid); }
  batchAddCustomers(customers: Omit<Customer, 'id' | 'createdAt'>[]): Promise<{ success: boolean; message: string }> { return this.customers.batchAddCustomers(customers); }
  exportCustomers(startDate?: string, endDate?: string): Promise<any[]> { return this.customers.exportCustomers(startDate, endDate); }
  
  uploadCustomerPhoto(customerId: string, photoData: string): Promise<string> { return this.customers.uploadCustomerPhoto(customerId, photoData); }
  uploadIdCardPhoto(customerId: string, photoData: string): Promise<string> { return this.customers.uploadIdCardPhoto(customerId, photoData); }

  // --- User / Employee Methods ---
  getUsers(): Promise<AppUser[]> { return this.users.getUsers(); }
  updateUser(updatedUser: AppUser): Promise<AppUser> { return this.users.updateUser(updatedUser); }
  deleteUser(uid: string): Promise<void> { return this.users.deleteUser(uid); }
  createInvitation(email: string, role: AppUser['role']): Promise<string> { return this.users.createInvitation(email, role); }
  getInvitationDetails(token: string): Promise<{ email: string, role: string } | null> { return this.users.getInvitationDetails(token); }
  getUserPermissions(): Promise<Permissions> { return this.users.getUserPermissions(); }
  ensureDataConsistency(): Promise<any> { return this.users.ensureDataConsistency(); }
  sendTestAlert(message: string): Promise<{ success: boolean; message: string }> { return this.users.sendTestAlert(message); }
  checkStoreHasDoctors(): Promise<boolean> { return this.users.checkStoreHasDoctors(); }

  // --- Store & POS Device Methods ---
  getStores(): Promise<Store[]> { return this.users.getStores(); }
  getPosDevices(): Promise<PosDevice[]> { return this.users.getPosDevices(); }
  addPosDevice(deviceData: Omit<PosDevice, 'id' | 'createdAt'>): Promise<PosDevice> { return this.users.addPosDevice(deviceData); }
  updatePosDevice(updatedDevice: PosDevice): Promise<PosDevice> { return this.users.updatePosDevice(updatedDevice); }
  deletePosDevice(deviceId: string): Promise<void> { return this.users.deletePosDevice(deviceId); }

  // --- Loyalty Methods ---
  getRewards(): Promise<LoyaltyReward[]> { return this.loyalty.getRewards(); }
  addReward(rewardData: Omit<LoyaltyReward, 'id' | 'createdAt'>): Promise<LoyaltyReward> { return this.loyalty.addReward(rewardData); }
  updateReward(rewardData: LoyaltyReward): Promise<LoyaltyReward> { return this.loyalty.updateReward(rewardData); }
  deleteReward(id: string): Promise<void> { return this.loyalty.deleteReward(id); }
  getLoyaltyTiers(): Promise<LoyaltyTier[]> { return this.loyalty.getLoyaltyTiers(); }
  addLoyaltyTier(tierData: Omit<LoyaltyTier, 'id' | 'createdAt'>): Promise<LoyaltyTier> { return this.loyalty.addLoyaltyTier(tierData); }
  updateLoyaltyTier(tierData: LoyaltyTier): Promise<LoyaltyTier> { return this.loyalty.updateLoyaltyTier(tierData); }
  deleteLoyaltyTier(id: number): Promise<void> { return this.loyalty.deleteLoyaltyTier(id); }

  // --- Core POS & Order Methods ---
  processOrder(ticket: Ticket, paymentMethod: string, total: number, discountAmount: number, shiftId: string, cashier: AppUser, loyaltySettings: LoyaltySettings | null): Promise<Order> { return this.orders.processOrder(ticket, paymentMethod, total, discountAmount, shiftId, cashier, loyaltySettings); }
  getOrder(orderId: string): Promise<Order | null> { return this.orders.getOrder(orderId); }
  getOrders(): Promise<Order[]> { return this.orders.getOrders(); }
  findOrderByNumber(orderNumber: string): Promise<Order | null> { return this.orders.findOrderByNumber(orderNumber); }
  voidOrderAndRestock(orderId: string, itemsToRestock: { productId: string, quantity: number }[]): Promise<void> { return this.orders.voidOrderAndRestock(orderId, itemsToRestock); }

  // --- Inventory Methods ---
  processInventoryForOrder(orderId: string): Promise<{ success: boolean; message?: string }> { return this.inventory.processInventoryForOrder(orderId); }
  adjustStock(productId: string, userId: string, changeAmount: number, reason: 'RESTOCK' | 'ADJUST_DAMAGE' | 'ADJUST_CORRECTION' | 'ADJUST_THEFT'): Promise<void> { return this.inventory.adjustStock(productId, userId, changeAmount, reason); }
  getInventoryHistory(productId: string): Promise<InventoryLog[]> { return this.inventory.getInventoryHistory(productId); }
  
  // --- Tele-Medicine & Consent Methods ---
  createPrescription(
      customerId: string, 
      storeId: string, 
      cashierId: string, 
      symptoms: string[] = [], 
      vitals?: VitalsData,
      referred_by_store_id?: string,
      referred_by_store_name?: string,
      referred_to_store_id?: string
  ): Promise<Prescription> { 
      return this.prescriptions.createPrescription(customerId, storeId, cashierId, symptoms, vitals, referred_by_store_id, referred_by_store_name, referred_to_store_id); 
  }
  
  async logConsent(customerId: string, consentType: string, consentVersion: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) {
      console.log(`Mock Consent Logged: ${customerId}, ${consentType}, ${consentVersion}`);
      return;
    }
    const { error } = await this.supabase.rpc('log_customer_consent', {
      p_customer_id: customerId,
      p_consent_type: consentType,
      p_consent_version: consentVersion
    });
    if (error) throw error;
  }
  
  createApprovedPrescription(customer: Customer, details: PrescriptionDetail, doctorId: string): Promise<Prescription> { return this.prescriptions.createApprovedPrescription(customer, details, doctorId); }
  getPrescription(prescriptionId: string): Promise<Prescription | null> { return this.prescriptions.getPrescription(prescriptionId); }
  getValidPrescriptionsForCustomer(customerId: string): Promise<CategoryQuota[]> { return this.prescriptions.getValidPrescriptionsForCustomer(customerId); }
  getPendingPrescriptions(): Promise<Prescription[]> { return this.prescriptions.getPendingPrescriptions(); }
  getApprovedPrescriptions(): Promise<Prescription[]> { return this.prescriptions.getApprovedPrescriptions(); }
  reviewPrescription(prescriptionId: string, status: 'APPROVED' | 'REJECTED', notes: string | null, approvedItems: PrescriptionDetail | null): Promise<void> { return this.prescriptions.reviewPrescription(prescriptionId, status, notes, approvedItems); }
  getPrescriptionForPrinting(prescriptionId: string): Promise<any | null> { return this.prescriptions.getPrescriptionForPrinting(prescriptionId); }
  cancelPrescription(prescriptionId: string): Promise<void> { return this.prescriptions.cancelPrescription(prescriptionId); }
  engagePrescription(prescriptionId: string): Promise<any> { return this.prescriptions.engagePrescription(prescriptionId); }
  releasePrescription(prescriptionId: string): Promise<void> { return this.prescriptions.releasePrescription(prescriptionId); }
  getCustomerHistory(customerId: string, page: number, pageSize: number): Promise<Prescription[]> { 
    return this.prescriptions.getCustomerHistory(customerId, page, pageSize); 
  }


  // --- Reporting Methods ---
  getSalesSummaryData(startDate: string, endDate: string): Promise<SalesSummary> { return this.reporting.getSalesSummaryData(startDate, endDate); }
  getAccountingReport(startDate: string, endDate: string): Promise<AccountingSummary> { return this.reporting.getAccountingReport(startDate, endDate); }
  getBestsellersReport(startDate: string, endDate: string, cashierId?: string): Promise<BestsellerItem[]> { return this.reporting.getBestsellersReport(startDate, endDate, cashierId); }
  getSalesSummaryReport(startDate: string, endDate: string, cashierId?: string): Promise<SalesSummaryReport> { return this.reporting.getSalesSummaryReport(startDate, endDate, cashierId); }
  getCategorySalesReport(startDate: string, endDate: string, cashierId?: string): Promise<CategorySalesReport[]> { return this.reporting.getCategorySalesReport(startDate, endDate, cashierId); }
  getEmployeeSalesReport(startDate: string, endDate: string, cashierId?: string): Promise<EmployeeSalesReport[]> { return this.reporting.getEmployeeSalesReport(startDate, endDate, cashierId); }
  getPaymentMethodSalesReport(startDate: string, endDate: string, cashierId?: string): Promise<PaymentMethodSalesReport[]> { return this.reporting.getPaymentMethodSalesReport(startDate, endDate, cashierId); }
  getDiscountsReport(startDate: string, endDate: string, cashierId?: string): Promise<DiscountReport[]> { return this.reporting.getDiscountsReport(startDate, endDate, cashierId); }
  getTaxesReport(startDate: string, endDate: string, cashierId?: string): Promise<TaxReport[]> { return this.reporting.getTaxesReport(startDate, endDate, cashierId); }
  getShiftsReport(startDate: string, endDate: string, cashierId?: string): Promise<ShiftReport[]> { return this.reporting.getShiftsReport(startDate, endDate, cashierId); }
  getTimeClockReport(startDate: string, endDate: string, cashierId?: string): Promise<TimeClockEntry[]> { return this.reporting.getTimeClockReport(startDate, endDate, cashierId); }
  getHoursWorkedSummary(startDate: string, endDate: string, cashierId?: string): Promise<HoursWorkedSummary[]> { return this.reporting.getHoursWorkedSummary(startDate, endDate, cashierId); }
  getClinicalSymptomStats(startDate: string, endDate: string): Promise<ClinicalSymptomStats[]> { return this.reporting.getClinicalSymptomStats(startDate, endDate); }
  getDoctorPerformance(startDate: string, endDate: string): Promise<DoctorPerformanceStats[]> { return this.reporting.getDoctorPerformance(startDate, endDate); }


  // --- Shift Management Methods ---
  getOpenShift(cashierId: string): Promise<Shift | null> { return this.shifts.getOpenShift(cashierId); }
  openShift(cashier: AppUser, openAmount: number): Promise<Shift> { return this.shifts.openShift(cashier, openAmount); }
  clockOut(shiftId: string, countedCash: number): Promise<Shift> { return this.shifts.clockOut(shiftId, countedCash); }

  // --- Billing & Subscription Methods ---
  getPlans(): Promise<Plan[]> { return this.billing.getPlans(); }
  getSubscription(): Promise<Subscription | null> { return this.billing.getSubscription(); }
  // FIX: Removed upgradeSubscription method as it is not present in BillingService and not used by components.
  getWalletBalance(): Promise<StoreWallet> { return this.billing.getWalletBalance(); }
  getWalletTransactions(): Promise<WalletTransaction[]> { return this.billing.getWalletTransactions(); }


  // --- Accounting Methods (New) ---
  getAccounts(): Promise<Account[]> { return this.accounting.getAccounts(); }
  addAccount(accountData: Omit<Account, 'id' | 'createdAt' | 'storeId'>): Promise<Account> { return this.accounting.addAccount(accountData); }
  updateAccount(updatedAccount: Account): Promise<Account> { return this.accounting.updateAccount(updatedAccount); }
  deleteAccount(accountId: string): Promise<void> { return this.accounting.deleteAccount(accountId); }

  // --- Partner Network Methods ---
  async getPartners(): Promise<StorePartner[]> {
    if (!isSupabaseConfigured() || !this.supabase) return [];
    const { data, error } = await this.supabase.rpc('get_my_partners');
    if (error) throw error;
    return (data || []) as StorePartner[];
  }

  async requestPartnerConnection(targetStoreId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) throw new Error("Database not connected");
    const { error } = await this.supabase.rpc('request_partner_connection', { p_target_store_id: targetStoreId });
    if (error) throw error;
  }

  async updatePartnerStatus(partnerId: string, status: 'ACTIVE' | 'REJECTED'): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) throw new Error("Database not connected");
    const { error } = await this.supabase.rpc('update_partner_status', { p_partner_id: partnerId, p_status: status });
    if (error) throw error;
  }
  
  // --- Maintenance Methods ---
  async runFixAuditLogMigration(): Promise<string> {
    if (!isSupabaseConfigured() || !this.supabase) throw new Error("Database not connected");
    const { data, error } = await this.supabase.rpc('fix_audit_log_function');
    if (error) throw error;
    return data as string;
  }
}
